import axios from 'axios';
import type { Customer, Asset, Device, Gateway, User } from '../context/ManagementContext';

const API_BASE_URL = import.meta.env.VITE_API_URL ? new URL('/api/v1/management', import.meta.env.VITE_API_URL).toString() : 'http://localhost:3000/api/v1/management';
const ADMIN_API_BASE_URL = import.meta.env.VITE_API_URL ? new URL('/api/v1/admin', import.meta.env.VITE_API_URL).toString() : 'http://localhost:3000/api/v1/admin';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const adminApi = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token interceptors for both APIs
const addAuthToken = (config: any) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(addAuthToken);
adminApi.interceptors.request.use(addAuthToken);


// API Response types
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Customer API
export const customerApi = {
  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    includeInactive?: boolean; 
    parentId?: string; 
    search?: string;
    status?: 'active' | 'inactive' | 'all';
    hierarchy?: 'top-level' | 'child' | 'all';
    sortBy?: 'name' | 'label' | 'createdAt' | 'updatedAt' | 'id';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Customer>> => {
    const response = await api.get('/customers', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Customer> => {
    const response = await api.get(`/customers/${id}`);
    return response.data.data;
  },

  create: async (data: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'isDeleted'>): Promise<Customer> => {
    const response = await api.post('/customers', data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Customer>): Promise<Customer> => {
    const response = await api.put(`/customers/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/customers/${id}`);
  },

  setParent: async (id: string, parentId: string | null): Promise<Customer> => {
    const response = await api.put(`/customers/${id}/parent`, { parentId });
    return response.data.data;
  },

  activate: async (id: string): Promise<Customer> => {
    const response = await api.post(`/customers/${id}/activate`);
    return response.data.data;
  },

  deactivate: async (id: string): Promise<Customer> => {
    const response = await api.post(`/customers/${id}/deactivate`);
    return response.data.data;
  },

  getChildren: async (id: string): Promise<Customer[]> => {
    const response = await api.get(`/customers/${id}/children`);
    return response.data.data;
  },
};

// Asset API
export const assetApi = {
  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    customerId?: string; 
    parentId?: string;
    search?: string; 
    includeInactive?: boolean; 
    status?: 'active' | 'inactive' | 'all';
    hierarchy?: 'top-level' | 'child' | 'all';
    sortBy?: 'name' | 'label' | 'createdAt' | 'updatedAt' | 'id';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Asset>> => {
    const response = await api.get('/assets', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Asset> => {
    const response = await api.get(`/assets/${id}`);
    return response.data.data;
  },

  create: async (data: Omit<Asset, 'id' | 'createdAt' | 'updatedAt'>): Promise<Asset> => {
    console.log('Creating asset with data:', data);
    const response = await api.post('/assets', data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Asset>): Promise<Asset> => {
    const response = await api.put(`/assets/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/assets/${id}`);
  },

  setParent: async (id: string, parentId: string | null): Promise<Asset> => {
    const response = await api.put(`/assets/${id}/parent`, { parentId });
    return response.data.data;
  },

  setCustomer: async (id: string, customerId: string | null): Promise<Asset> => {
    const response = await api.put(`/assets/${id}/customer`, { customerId });
    return response.data.data;
  },

  // Device association methods
  getDevices: async (id: string): Promise<Device[]> => {
    const response = await api.get(`/assets/${id}/devices`);
    return response.data.data;
  },

  associateDevices: async (id: string, deviceIds: string[]): Promise<void> => {
    await api.post(`/assets/${id}/devices`, { deviceIds });
  },

  replaceDevices: async (id: string, deviceIds: string[]): Promise<void> => {
    await api.put(`/assets/${id}/devices`, { deviceIds });
  },

  disassociateDevices: async (id: string, deviceIds: string[]): Promise<void> => {
    await api.delete(`/assets/${id}/devices`, { data: { deviceIds } });
  },
};

// Device API
export const deviceApi = {
  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    customerId?: string; 
    assetId?: string; 
    gatewayId?: string; 
    search?: string; 
    includeInactive?: boolean; 
    status?: 'active' | 'inactive' | 'all';
    deviceType?: 'energy' | 'water';
    sortBy?: 'name' | 'label' | 'createdAt' | 'updatedAt' | 'id';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Device>> => {
    const response = await api.get('/devices', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Device> => {
    const response = await api.get(`/devices/${id}`);
    return response.data.data;
  },

  create: async (data: Omit<Device, 'createdAt' | 'updatedAt'>): Promise<Device> => {
    const response = await api.post('/devices', data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Device>): Promise<Device> => {
    const response = await api.put(`/devices/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/devices/${id}`);
  },

  setAsset: async (id: string, assetId: string | null): Promise<Device> => {
    const response = await api.put(`/devices/${id}/asset`, { assetId });
    return response.data.data;
  },

  setCustomer: async (id: string, customerId: string | null): Promise<Device> => {
    const response = await api.put(`/devices/${id}/customer`, { customerId });
    return response.data.data;
  },

  // Device name population methods
  batchPopulateNames: async (onlyMissingNames: boolean = true): Promise<{
    success: boolean;
    data: {
      totalDevices: number;
      successCount: number;
      failureCount: number;
      skippedCount: number;
      results: Array<{
        deviceId: string;
        success: boolean;
        previousName?: string;
        newName?: string;
        error?: string;
        skipped?: boolean;
        skipReason?: string;
      }>;
      gatewayErrors: Array<{ gatewayId: string; error: string }>;
    };
    message: string;
  }> => {
    const response = await api.post('/devices/populate-names', { onlyMissingNames });
    return response.data;
  },

  populateName: async (id: string): Promise<{
    success: boolean;
    data: {
      deviceId: string;
      success: boolean;
      previousName?: string;
      newName?: string;
      error?: string;
    };
    message?: string;
    error?: string;
  }> => {
    const response = await api.post(`/devices/${id}/populate-name`);
    return response.data;
  },

  getPopulationStatus: async (): Promise<{
    success: boolean;
    data: {
      totalDevices: number;
      devicesWithNames: number;
      devicesWithoutNames: number;
      devicesByType: Record<string, { total: number; withNames: number; withoutNames: number }>;
    };
  }> => {
    const response = await api.get('/devices/populate-status');
    return response.data;
  },

  populateGatewayNames: async (gatewayId: string, onlyMissingNames: boolean = true): Promise<{
    success: boolean;
    data: {
      totalDevices: number;
      successCount: number;
      failureCount: number;
      skippedCount: number;
      results: Array<{
        deviceId: string;
        success: boolean;
        previousName?: string;
        newName?: string;
        error?: string;
        skipped?: boolean;
        skipReason?: string;
      }>;
      gatewayErrors: Array<{ gatewayId: string; error: string }>;
    };
    message: string;
  }> => {
    const response = await api.post(`/devices/populate-gateway-names/${gatewayId}`, { onlyMissingNames });
    return response.data;
  },

  bulkAssignCustomer: async (deviceIds: string[], customerId: string | null): Promise<{
    data: Device[];
    message: string;
  }> => {
    const response = await api.put('/devices/bulk-assign-customer', { deviceIds, customerId });
    return response.data;
  },
};

// Gateway API
export const gatewayApi = {
  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    customerId?: string; 
    assetId?: string; 
    search?: string; 
    isOnline?: boolean; 
    includeDeleted?: boolean; 
    connectionStatus?: 'online' | 'offline' | 'all';
    status?: 'running' | 'paused' | 'registered' | 'unregistered' | 'all';
    sortBy?: 'name' | 'id' | 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Gateway>> => {
    const response = await api.get('/gateways', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Gateway> => {
    const response = await api.get(`/gateways/${id}`);
    return response.data.data;
  },

  create: async (data: Omit<Gateway, 'createdAt' | 'updatedAt'>): Promise<Gateway> => {
    const response = await api.post('/gateways', data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<Gateway>): Promise<Gateway> => {
    const response = await api.put(`/gateways/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/gateways/${id}`);
  },

  setCustomer: async (id: string, customerId: string | null): Promise<Gateway> => {
    const response = await api.put(`/gateways/${id}/customer`, { customerId });
    return response.data.data;
  },

  setAsset: async (id: string, assetId: string | null): Promise<Gateway> => {
    const response = await api.put(`/gateways/${id}/asset`, { assetId });
    return response.data.data;
  },

  resetSyncTimestamps: async (id: string, data: {
    lastEnergyFetchTimestamp?: string;
    lastWaterFetchTimestamp?: string;
  }): Promise<Gateway> => {
    const response = await api.put(`/gateways/${id}/sync-timestamps`, data);
    return response.data.data;
  },
};

// Management API
export const managementApi = {
  getHierarchy: async (): Promise<any[]> => {
    const response = await api.get('/hierarchy');
    return response.data.data;
  },

  getStatistics: async (): Promise<{
    totalCustomers: number;
    totalAssets: number;
    totalDevices: number;
    totalGateways: number;
    activeCustomers: number;
    activeAssets: number;
    activeDevices: number;
    activeGateways: number;
  }> => {
    const response = await api.get('/statistics');
    const data = response.data.data;
    
    // Add safety checks for the data structure
    if (!data || !data.customers || !data.assets || !data.devices || !data.gateways) {
      console.error('Invalid statistics response structure:', response.data);
      throw new Error('Invalid statistics response structure');
    }
    
    // Transform backend response to match frontend expectations
    return {
      totalCustomers: data.customers.total || 0,
      totalAssets: data.assets.total || 0,
      totalDevices: data.devices.total || 0,
      totalGateways: data.gateways.total || 0,
      activeCustomers: data.customers.active || 0,
      activeAssets: data.assets.active || 0,
      activeDevices: data.devices.active || 0,
      activeGateways: data.gateways.online || 0, // backend uses 'online' for gateways
    };
  },

  validateCircularReference: async (entityType: 'customer' | 'asset', entityId: string, parentId: string): Promise<boolean> => {
    const response = await api.get('/validate/circular-reference', {
      params: { entityType, entityId, parentId }
    });
    return response.data.data.wouldCreateCircularReference;
  },

  validateIntegrity: async (): Promise<any> => {
    const response = await api.get('/validate/integrity');
    return response.data.data;
  },
};

// User API
export const userApi = {
  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    includeInactive?: boolean; 
    role?: 'admin';
    search?: string;
    sortBy?: 'name' | 'email' | 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<User>> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  },

  create: async (data: { 
    email: string; 
    name: string; 
    password: string; 
    role?: 'admin' 
  }): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data.data;
  },

  update: async (id: string, data: { 
    email?: string; 
    name?: string; 
    password?: string; 
    role?: 'admin';
    isActive?: boolean;
  }): Promise<User> => {
    const response = await api.put(`/users/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  activate: async (id: string): Promise<User> => {
    const response = await api.post(`/users/${id}/activate`);
    return response.data.data;
  },

  deactivate: async (id: string): Promise<User> => {
    const response = await api.post(`/users/${id}/deactivate`);
    return response.data.data;
  },

  changePassword: async (id: string, data: { 
    currentPassword: string; 
    newPassword: string 
  }): Promise<{ message: string }> => {
    const response = await api.put(`/users/${id}/password`, data);
    return response.data;
  },
};

// Aggregate Refresh API
export const aggregateRefreshApi = {
  getJobs: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    gatewayId?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminApi.get('/refresh-aggregates', { params });
    return response.data;
  },

  getJobStatus: async (jobId: string): Promise<any> => {
    const response = await adminApi.get(`/refresh-aggregates/${jobId}`);
    return response.data.data;
  },

  getJobLogs: async (jobId: string, params?: {
    page?: number;
    limit?: number;
    level?: string;
  }): Promise<PaginatedResponse<any>> => {
    const response = await adminApi.get(`/refresh-aggregates/${jobId}/logs`, { params });
    return response.data;
  },

  createJob: async (data: {
    startDate: string;
    endDate: string;
    batchSizeDays?: number;
    delaySeconds?: number;
  }): Promise<any> => {
    const response = await adminApi.post('/refresh-aggregates', data);
    return response.data.data;
  },

  cancelJob: async (jobId: string): Promise<{ message: string }> => {
    const response = await adminApi.delete(`/refresh-aggregates/${jobId}`);
    return response.data;
  },

  getSystemHealth: async (): Promise<any> => {
    const response = await adminApi.get('/system-health');
    return response.data.data;
  },

  cleanupOldJobs: async (): Promise<{ message: string }> => {
    const response = await adminApi.post('/refresh-aggregates/cleanup');
    return response.data;
  },
};

// System Settings API
export const systemSettingsApi = {
  getAll: async (): Promise<any[]> => {
    const response = await adminApi.get('/settings');
    return response.data.data;
  },

  getByKey: async (key: string): Promise<any> => {
    const response = await adminApi.get(`/settings/${key}`);
    return response.data.data;
  },

  update: async (key: string, data: { value: any; description?: string }): Promise<any> => {
    const response = await adminApi.put(`/settings/${key}`, data);
    return response.data.data;
  },

  delete: async (key: string): Promise<{ message: string }> => {
    const response = await adminApi.delete(`/settings/${key}`);
    return response.data;
  },

  getDefaults: async (): Promise<any[]> => {
    const response = await adminApi.get('/settings/defaults');
    return response.data.data;
  },

  initialize: async (): Promise<{ message: string; data: any[] }> => {
    const response = await adminApi.post('/settings/initialize');
    return response.data;
  },
};

// API Client Management Types
export interface ApiClient {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  scopes: string[];
  customerId?: string;
  customer?: { id: string; name: string };
  isActive: boolean;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
  clientSecret?: string; // Only present in create response
}

export interface CreateApiClientRequest {
  clientId: string;
  clientSecret: string;
  name: string;
  description?: string;
  scopes: string[];
  customerId?: string;
}

export interface UpdateApiClientRequest {
  name?: string;
  description?: string;
  scopes?: string[];
  customerId?: string;
  isActive?: boolean;
}

// API Client Management API
export const apiClientApi = {
  getAll: async (page = 1, limit = 10, search?: string): Promise<{
    clients: ApiClient[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> => {
    const response = await adminApi.get('/api-clients', {
      params: { page, limit, search }
    });
    return response.data;
  },

  getById: async (id: string): Promise<ApiClient> => {
    const response = await adminApi.get(`/api-clients/${id}`);
    return response.data;
  },

  create: async (data: CreateApiClientRequest): Promise<ApiClient> => {
    const response = await adminApi.post('/api-clients', data);
    return response.data;
  },

  update: async (id: string, data: UpdateApiClientRequest): Promise<ApiClient> => {
    const response = await adminApi.put(`/api-clients/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await adminApi.delete(`/api-clients/${id}`);
    return response.data;
  },

  viewSecret: async (id: string): Promise<{ clientSecret: string }> => {
    const response = await adminApi.get(`/api-clients/${id}/secret`);
    return response.data;
  },

  regenerateSecret: async (id: string): Promise<{ message: string; clientSecret: string }> => {
    const response = await adminApi.post(`/api-clients/${id}/regenerate-secret`);
    return response.data;
  },

  getRateLimits: async (id: string): Promise<{
    defaults: Record<string, number>;
    overrides: Partial<Record<string, number>>;
    effective: Record<string, number>;
  }> => {
    const response = await adminApi.get(`/api-clients/${id}/rate-limits`);
    return response.data;
  },

  updateRateLimits: async (id: string, overrides: Record<string, number>): Promise<{
    message: string;
    overrides: Record<string, number>;
  }> => {
    const response = await adminApi.put(`/api-clients/${id}/rate-limits`, overrides);
    return response.data;
  },

  resetRateLimits: async (id: string): Promise<{
    message: string;
    defaults: Record<string, number>;
  }> => {
    const response = await adminApi.delete(`/api-clients/${id}/rate-limits`);
    return response.data;
  },

  getClientsSummary: async (): Promise<Array<{
    clientId: string;
    requestsToday: number;
    errorsToday: number;
    rateLimitHitsToday: number;
    errorRateToday: number;
    lastUsedAt: string | null;
  }>> => {
    const response = await adminApi.get('/api-request-logs/clients-summary');
    return response.data;
  },

  getClientDetailedStats: async (id: string): Promise<{
    clientId: string;
    period: { from: string; to: string };
    totalRequests: number;
    avgResponseTimeMs: number | null;
    dailyStats: Array<{
      date: string;
      totalRequests: number;
      errors: number;
      rateLimitHits: number;
      avgResponseTime: number | null;
    }>;
    topEndpoints: Array<{
      endpoint: string;
      count: number;
      avgResponseTime: number | null;
      errors: number;
    }>;
    tierUsage: Array<{
      tier: string;
      count: number;
      avgResponseTime: number | null;
    }>;
    errorBreakdown: Array<{
      statusCode: number;
      count: number;
    }>;
  }> => {
    const response = await adminApi.get(`/api-request-logs/client/${id}/detailed`);
    return response.data;
  },
};

export default {
  customer: customerApi,
  asset: assetApi,
  device: deviceApi,
  gateway: gatewayApi,
  user: userApi,
  management: managementApi,
  aggregateRefresh: aggregateRefreshApi,
  systemSettings: systemSettingsApi,
  apiClient: apiClientApi,
};

// Export the extended API with backward compatibility
export const extendedManagementApi = {
  ...managementApi,
  getApiClients: apiClientApi.getAll,
  getApiClient: apiClientApi.getById,
  createApiClient: apiClientApi.create,
  updateApiClient: apiClientApi.update,
  deleteApiClient: apiClientApi.delete,
  viewApiClientSecret: apiClientApi.viewSecret,
  regenerateApiClientSecret: apiClientApi.regenerateSecret,
  getApiClientRateLimits: apiClientApi.getRateLimits,
  updateApiClientRateLimits: apiClientApi.updateRateLimits,
  resetApiClientRateLimits: apiClientApi.resetRateLimits,
  getClientsSummary: apiClientApi.getClientsSummary,
  getClientDetailedStats: apiClientApi.getClientDetailedStats,
};